/**
 * Guest (unauthenticated) booking price quote.
 *
 * The counterpart to `useBookingValidation` for a booker with no account. Callers
 * should reach for `useBookingPricing`, which picks between the two — this exists
 * on its own so the guest path can be tested and cached independently.
 *
 * Takes no `userId`: the endpoint is unauthenticated and ignores one.
 *
 * @module booking/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { bookingService } from '../api/bookingService';

export interface UseBookingQuoteOptions {
  clubId: number | null | undefined;
  bayId: number | null | undefined;
  /** ISO 8601 UTC */
  startTime: string | null | undefined;
  /** ISO 8601 UTC */
  endTime: string | null | undefined;
  guestCount?: number;
  /** Priced loosely here — an anonymous caller has no per-user history. */
  promoCode?: string;
  enabled?: boolean;
}

export function useBookingQuote(options: UseBookingQuoteOptions) {
  const { clubId, bayId, startTime, endTime, guestCount, promoCode, enabled = true } = options;

  const hasRequiredParams = !!clubId && !!bayId && !!startTime && !!endTime;

  return useQuery({
    queryKey: hasRequiredParams
      ? queryKeys.bookings.guestQuote(clubId, { bayId, startTime, endTime, guestCount, promoCode })
      : queryKeys.disabled.bookingQuote(),
    queryFn: async () => {
      if (!clubId || !bayId || !startTime || !endTime) {
        throw new Error('Required parameters missing');
      }

      const result = await bookingService.quoteBooking({ clubId, bayId, startTime, endTime, guestCount, promoCode });

      // A `success: false` here is a transport or contract failure, not a refusal —
      // the server answers 200 with `isValid: false` when it declines the booking.
      // Throwing puts it on React Query's error path, where the caller blocks submit.
      if (!result.success) {
        throw new Error(result.message || 'Booking quote failed');
      }

      return result.payload;
    },
    enabled: enabled && hasRequiredParams,
    ...cachePresets.frequent,
  });
}
